import { useCallback, useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useFeatureFlags } from '@/hooks/useFeatureFlag';
import { usePermissions } from '@/hooks/usePermissions';
import { useUserRole } from '@/hooks/useUserRole';
import { cn } from '@/lib/utils';
import {
  adminMenuGroups,
  type AdminMenuGroup,
  type AdminMenuItem,
  type AdminMenuRole,
} from './adminMenuData';

interface AdminMenuProps {
  pendingApplicationsCount: number;
  onSignOut: () => void;
}

const featureFlagKeys = Array.from(
  new Set(
    adminMenuGroups.flatMap(group => group.items.map(item => item.featureFlag).filter(Boolean))
  )
) as string[];

const AdminMenu = ({ pendingApplicationsCount, onSignOut }: AdminMenuProps) => {
  const location = useLocation();
  const { flags } = useFeatureFlags(featureFlagKeys);
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const { hasRole, isLoading: roleLoading } = useUserRole();

  const activeRoles = useMemo<AdminMenuRole[]>(() => {
    const roles: AdminMenuRole[] = [
      'admin',
      'super_admin',
      'campaign_manager',
      'bud',
      'business_unit_director',
      'supervisor',
      'finance_viewer',
      'line_manager',
      'finance',
      'brand_ambassador',
    ];
    return roles.filter((role) => hasRole(role));
  }, [hasRole]);

  const canSeeItem = useCallback(
    (item: AdminMenuItem) => {
      if (item.hidden) return false;
      if (item.featureFlag && flags[item.featureFlag] !== true) return false;

      // Role-based visibility: items can declare requiredRoles and we compare against
      // the user's normalized org role plus the super_admin pseudo-role above.
      if (item.requiredRoles?.length) {
        if (roleLoading) return false;
        if (!item.requiredRoles.some(role => activeRoles.includes(role))) return false;
      }

      // Permission-based visibility: only render items when permissions confirm access.
      if (item.requiredPermissions?.length) {
        if (permissionsLoading) return false;
        if (!item.requiredPermissions.every(perm => hasPermission(perm))) return false;
      }

      return true;
    },
    [activeRoles, flags, hasPermission, permissionsLoading, roleLoading]
  );

  const visibleMenuGroups = useMemo<AdminMenuGroup[]>(
    () =>
      adminMenuGroups
        .map(group => ({
          ...group,
          items: group.items.filter((item) => canSeeItem(item)),
        }))
        .filter(group => group.items.length > 0),
    [canSeeItem]
  );

  const isItemActive = useCallback(
    (item: AdminMenuItem) => {
      if (item.end) return location.pathname === item.href;
      return location.pathname === item.href || location.pathname.startsWith(`${item.href}/`);
    },
    [location.pathname]
  );

  const activeGroupIds = useMemo(
    () =>
      visibleMenuGroups
        .filter(group => group.items.some((item) => isItemActive(item)))
        .map(group => group.id),
    [isItemActive, visibleMenuGroups]
  );

  const [openGroups, setOpenGroups] = useState<string[]>([]);

  useEffect(() => {
    if (visibleMenuGroups.length === 0) return;
    const next = activeGroupIds.length > 0 ? activeGroupIds : [visibleMenuGroups[0].id];
    setOpenGroups(prev => {
      if (next.length === prev.length && next.every((value, index) => value === prev[index])) {
        return prev;
      }
      return next;
    });
  }, [activeGroupIds, visibleMenuGroups]);

  return (
    <div className="flex h-full flex-col">
      <Accordion
        type="multiple"
        value={openGroups}
        onValueChange={setOpenGroups}
        className="space-y-2"
      >
        {visibleMenuGroups.map(group => (
          <AccordionItem key={group.id} value={group.id} className="border-none">
            <AccordionTrigger className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:no-underline">
              {group.label}
            </AccordionTrigger>
            <AccordionContent className="space-y-1 px-1">
              {group.items.map(item => {
                const Icon = item.icon;
                return (
                  <Button
                    key={item.href}
                    asChild
                    variant="ghost"
                    className="w-full justify-start"
                  >
                    <NavLink
                      to={item.href}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          'w-full justify-start',
                          isActive && 'bg-accent text-accent-foreground'
                        )
                      }
                    >
                      <Icon className="mr-2 h-4 w-4" />
                      {item.label}
                      {item.badge === 'pendingApplications' &&
                        pendingApplicationsCount > 0 && (
                          <Badge className="ml-auto" variant="secondary">
                            {pendingApplicationsCount}
                          </Badge>
                        )}
                    </NavLink>
                  </Button>
                );
              })}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>

      <div className="mt-4 border-t pt-4">
        <Button variant="ghost" className="w-full justify-start" onClick={onSignOut}>
          <LogOut className="mr-2 h-4 w-4" />
          Sign Out
        </Button>
      </div>
    </div>
  );
};

export default AdminMenu;
